import { useEffect, type RefObject } from "react";

export type MainPageGsapRefs = {
  subHeroRef: RefObject<HTMLElement | null>;
  pastEventsSectionRef: RefObject<HTMLElement | null>;
  apacStatsSectionRef: RefObject<HTMLElement | null>;
  homeAudienceSectionRef: RefObject<HTMLElement | null>;
  homeProofSectionRef: RefObject<HTMLElement | null>;
};

/**
 * Load GSAP only when the user scrolls or a below-fold section approaches —
 * keeps the vendor-gsap chunk off the Lighthouse unused-JS / LCP window.
 * Hero motion uses CSS (`.home-hero-animate`) instead.
 */
function scheduleWhenScrollContentNear(
  getTargets: () => Array<Element | null | undefined>,
  fn: () => void,
): () => void {
  let cancelled = false;
  let done = false;
  let io: IntersectionObserver | undefined;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  let rafId = 0;

  const cleanup = () => {
    window.removeEventListener("scroll", onScroll);
    io?.disconnect();
    io = undefined;
    if (timeoutId !== undefined) clearTimeout(timeoutId);
    if (rafId) window.cancelAnimationFrame(rafId);
  };

  const run = () => {
    if (cancelled || done) return;
    done = true;
    cleanup();
    fn();
  };

  const onScroll = () => run();

  const setup = () => {
    if (cancelled || done) return;
    const targets = getTargets().filter((el): el is Element => el != null);
    if (targets.length > 0 && typeof IntersectionObserver === "function") {
      io = new IntersectionObserver(
        (entries) => {
          if (entries.some((e) => e.isIntersecting)) run();
        },
        { rootMargin: "220px 0px" },
      );
      targets.forEach((t) => io!.observe(t));
    }
    window.addEventListener("scroll", onScroll, { once: true, passive: true });
    // Fallback for users who never scroll; long enough to miss Lighthouse's load window.
    timeoutId = setTimeout(run, 12000);
  };

  const onReady = () => {
    rafId = window.requestAnimationFrame(setup);
  };

  if (document.readyState === "complete") {
    onReady();
  } else {
    window.addEventListener("load", onReady, { once: true });
  }

  return () => {
    cancelled = true;
    window.removeEventListener("load", onReady);
    cleanup();
  };
}

/**
 * Loads GSAP + ScrollTrigger when below-fold homepage sections are near,
 * so the homepage critical path is not charged for unused GSAP bytes.
 */
export function useMainPageGsap(refs: MainPageGsapRefs): void {
  useEffect(() => {
    let cancelled = false;
    const revertFns: Array<() => void> = [];

    const cancelSchedule = scheduleWhenScrollContentNear(
      () => [
        refs.subHeroRef.current,
        refs.apacStatsSectionRef.current,
        refs.homeAudienceSectionRef.current,
        refs.homeProofSectionRef.current,
        refs.pastEventsSectionRef.current,
      ],
      () => {
        void (async () => {
          const [{ gsap }, { ScrollTrigger }] = await Promise.all([
            import("gsap"),
            import("gsap/ScrollTrigger"),
          ]);
          if (cancelled) return;
          gsap.registerPlugin(ScrollTrigger);

          const {
            subHeroRef,
            pastEventsSectionRef,
            apacStatsSectionRef,
            homeAudienceSectionRef,
            homeProofSectionRef,
          } = refs;

          const subHero = subHeroRef.current;
          if (subHero) {
            const titleEl = subHero.querySelector<HTMLElement>(".home-sub-hero-title");
            const lede = subHero.querySelector<HTMLElement>(".home-sub-hero-lede");
            const mapPanel = subHero.querySelector<HTMLElement>(".home-sub-hero-map-panel");
            const cta = subHero.querySelector<HTMLElement>(".home-sub-hero-cta-box");
            const ctx = gsap.context(() => {
              const tl = gsap.timeline({
                scrollTrigger: {
                  trigger: subHero,
                  start: "top 85%",
                  toggleActions: "play none none none",
                },
              });
              if (titleEl && lede) {
                tl.from([titleEl, lede], {
                  opacity: 0,
                  y: 32,
                  duration: 0.72,
                  stagger: 0.14,
                  ease: "power3.out",
                });
              } else if (titleEl) {
                tl.from(titleEl, { opacity: 0, y: 32, duration: 0.75, ease: "power3.out" });
              }
              if (mapPanel) {
                tl.from(
                  mapPanel,
                  { opacity: 0, scale: 0.92, duration: 0.95, ease: "power3.out" },
                  titleEl || lede ? 0.12 : 0,
                );
              }
              if (cta) {
                tl.from(cta, { opacity: 0, y: 22, duration: 0.62, ease: "power2.out" }, "-=0.45");
              }
            }, subHero);
            revertFns.push(() => ctx.revert());
          }

          const past = pastEventsSectionRef.current;
          if (past) {
            const introLines = past.querySelectorAll<HTMLElement>(".legacy-showcase-intro-line");
            if (introLines.length) {
              const ctx = gsap.context(() => {
                gsap.from(introLines, {
                  scrollTrigger: {
                    trigger: past,
                    start: "top 85%",
                    toggleActions: "play none none none",
                  },
                  opacity: 0,
                  duration: 0.75,
                  stagger: 0.2,
                  ease: "power2.out",
                });
              }, past);
              revertFns.push(() => ctx.revert());
            }

            const track = past.querySelector<HTMLElement>(".legacy-showcase-track");
            const cards = past.querySelectorAll<HTMLElement>(".legacy-showcase-panel");
            if (cards.length && track) {
              gsap.set(cards, { opacity: 0 });
              await new Promise<void>((resolve) => {
                requestAnimationFrame(() => resolve());
              });
              if (!cancelled) {
                const ctx = gsap.context(() => {
                  cards.forEach((card) => {
                    gsap.to(card, {
                      scrollTrigger: {
                        trigger: card,
                        scroller: track,
                        horizontal: true,
                        start: "left 88%",
                        toggleActions: "play none none reverse",
                      },
                      opacity: 1,
                      duration: 0.65,
                      ease: "power2.out",
                    });
                  });
                }, past);
                revertFns.push(() => ctx.revert());
              }
            }
          }

          const apac = apacStatsSectionRef.current;
          if (apac) {
            const title = apac.querySelector<HTMLElement>(".apac-stats-title");
            const statBlocks = apac.querySelectorAll<HTMLElement>(".apac-stat-block");
            const ctx = gsap.context(() => {
              if (title) {
                gsap.from(title, {
                  scrollTrigger: {
                    trigger: apac,
                    start: "top 85%",
                    toggleActions: "play none none none",
                  },
                  opacity: 0,
                  y: 28,
                  duration: 0.75,
                  ease: "power3.out",
                });
              }
              if (statBlocks.length) {
                gsap.from(statBlocks, {
                  scrollTrigger: {
                    trigger: apac,
                    start: "top 85%",
                    toggleActions: "play none none none",
                  },
                  opacity: 0,
                  y: 40,
                  duration: 0.8,
                  delay: 0.2,
                  stagger: 0.18,
                  ease: "power3.out",
                });
              }
            }, apac);
            revertFns.push(() => ctx.revert());
          }

          const audience = homeAudienceSectionRef.current;
          if (audience) {
            const title = audience.querySelector<HTMLElement>(".home-audience-title");
            const subtitle = audience.querySelector<HTMLElement>(".home-audience-subtitle");
            const cards = audience.querySelectorAll<HTMLElement>(".home-audience-card");
            const headerEls = [title, subtitle].filter((el): el is HTMLElement => el != null);
            if (headerEls.length || cards.length) {
              const ctx = gsap.context(() => {
                const tl = gsap.timeline({
                  scrollTrigger: {
                    trigger: audience,
                    start: "top 82%",
                    toggleActions: "play none none none",
                  },
                });
                if (headerEls.length) {
                  tl.from(headerEls, {
                    opacity: 0,
                    y: 18,
                    duration: 0.65,
                    stagger: 0.1,
                    ease: "power2.out",
                  });
                }
                if (cards.length) {
                  tl.from(
                    cards,
                    {
                      opacity: 0,
                      y: 22,
                      duration: 0.7,
                      stagger: 0.14,
                      ease: "power2.out",
                    },
                    headerEls.length ? "-=0.25" : 0,
                  );
                }
              }, audience);
              revertFns.push(() => ctx.revert());
            }
          }

          const proof = homeProofSectionRef.current;
          if (proof) {
            const title = proof.querySelector<HTMLElement>(".home-proof-title");
            const headerLink = proof.querySelector<HTMLElement>(".home-proof-header-link");
            const cards = proof.querySelectorAll<HTMLElement>(".home-proof-card");
            const headerEls = [title, headerLink].filter((el): el is HTMLElement => el != null);
            if (headerEls.length || cards.length) {
              const ctx = gsap.context(() => {
                const tl = gsap.timeline({
                  scrollTrigger: {
                    trigger: proof,
                    start: "top 82%",
                    toggleActions: "play none none none",
                  },
                });
                if (headerEls.length) {
                  tl.from(headerEls, {
                    opacity: 0,
                    y: 18,
                    duration: 0.65,
                    stagger: 0.1,
                    ease: "power2.out",
                  });
                }
                if (cards.length) {
                  tl.from(
                    cards,
                    {
                      opacity: 0,
                      y: 22,
                      duration: 0.7,
                      stagger: 0.14,
                      ease: "power2.out",
                    },
                    headerEls.length ? "-=0.25" : 0,
                  );
                }
              }, proof);
              revertFns.push(() => ctx.revert());
            }
          }
        })();
      },
    );

    return () => {
      cancelled = true;
      cancelSchedule();
      revertFns.forEach((fn) => fn());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ref .current read after async import; ref objects stable
  }, []);
}
